/**
 * One bundle, from the teacher's side: what it says, what is in it, and how it
 * is going.
 *
 * ── Order is written whole, on every change ─────────────────────────────────
 * `PUT /bundles/:id/courses` states the entire contents in order, so moving one
 * course up sends the new list rather than a swap. The alternative — collecting
 * edits behind a Save button — means a teacher who reorders four courses and
 * closes the tab has done nothing, and a bundle whose order on screen is not
 * the order learners are being gated by.
 *
 * ── Two reads, on purpose ───────────────────────────────────────────────────
 * `GET /bundles/:id` carries the ordered contents and `GET /bundles/:id/report`
 * carries the numbers. The contents are also the report's row labels, and one
 * route serving both would have to decide whether a teacher who cannot see the
 * report may still see the order.
 */

import { useState } from 'react';
import { teach, learn } from '../api';
import { AssignDialog } from '../components/AssignDialog';
import { CourseMark, StatRow } from '../components/Bits';
import { ErrorNote, Footnote, Loading, OkNote, PageTitle, Panel, PanelHead } from '../components/Chrome';
import { BUNDLE_COLOURS } from '../components/NewBundleDialog';
import { ds } from '../ds';
import { Link, useRouter } from '../router';
import { HAIR, figure, meta, monoPlain, plural } from '../theme';
import { useActions, useLoad } from '../useLoad';

export default function BundleEditor({ id }: { id: string }) {
  const { Button, IconButton, Input, Select, Stat, Switch } = ds();
  const { navigate } = useRouter();

  const { data, error, loading, reload } = useLoad(
    () => learn.bundle(id),
    'Could not load this bundle.',
    id
  );
  const report = useLoad(() => teach.bundleReport(id), 'Could not load the bundle report.', id);
  const catalogue = useLoad(() => teach.courses(), 'Could not load your courses.');
  const { busy, error: wrote, ok, act, setOk } = useActions(reload);

  const [assigning, setAssigning] = useState(false);
  const [title, setTitle] = useState<string | null>(null);
  const [description, setDescription] = useState<string | null>(null);
  const [adding, setAdding] = useState('');

  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!data) return loading ? <Loading /> : null;

  const { bundle, courses } = data;
  const order = courses.map((c) => c.course.id);

  const setOrder = (next: string[], done: string) =>
    void act(async () => {
      await teach.setBundleCourses(id, { courseIds: next });
      await report.reload();
    }, done);

  const move = (index: number, by: number) => {
    const next = [...order];
    const target = index + by;
    if (target < 0 || target >= next.length) return;
    const moved = next[index]!;
    next[index] = next[target]!;
    next[target] = moved;
    setOrder(next, 'The order is saved.');
  };

  const notInBundle = (catalogue.data?.courses ?? []).filter((row) => !order.includes(row.card.id));

  return (
    <>
      <PageTitle
        title={bundle.title}
        meta={meta(
          plural(bundle.courseCount, 'course'),
          bundle.ordered ? 'in order' : null,
          bundle.state === 'live' ? 'live' : bundle.state
        )}
      >
        <Button variant="ghost" size="sm" onClick={() => navigate(`/bundle/${bundle.id}`)}>
          See it as a learner
        </Button>
        <Button variant="secondary" size="sm" onClick={() => setAssigning(true)}>
          Assign this bundle
        </Button>
      </PageTitle>

      <ErrorNote>{wrote ?? report.error}</ErrorNote>
      <OkNote>{ok}</OkNote>

      <Panel style={{ display: 'grid', gap: 16 }}>
        <PanelHead title="About" />

        <Input
          label="Title"
          value={title ?? bundle.title}
          disabled={busy}
          onChange={(e) => setTitle(e.target.value)}
        />
        <Input
          label="Description"
          multiline
          rows={3}
          value={description ?? bundle.description ?? ''}
          disabled={busy}
          onChange={(e) => setDescription(e.target.value)}
        />

        <div style={{ display: 'grid', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-strong)' }}>Colour</span>
          <div style={{ display: 'flex', gap: 8 }}>
            {BUNDLE_COLOURS.map((c, i) => (
              <button
                key={c}
                type="button"
                aria-label={`Colour ${i + 1}`}
                aria-pressed={bundle.colour === c}
                disabled={busy}
                onClick={() => void act(() => teach.updateBundle(id, { colour: c }), 'Colour saved.')}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 9,
                  background: c,
                  border: bundle.colour === c ? '2px solid var(--text-strong)' : '2px solid transparent'
                }}
              />
            ))}
          </div>
        </div>

        <Switch
          label="Courses unlock in order"
          checked={bundle.ordered}
          disabled={busy}
          onChange={(next) =>
            void act(
              () => teach.updateBundle(id, { ordered: next }),
              next ? 'Courses unlock in order.' : 'Courses can be taken in any order.'
            )
          }
        />

        <div>
          <Button
            variant="secondary"
            size="sm"
            disabled={
              busy ||
              ((title ?? bundle.title) === bundle.title &&
                (description ?? bundle.description ?? '') === (bundle.description ?? ''))
            }
            onClick={() =>
              void act(async () => {
                await teach.updateBundle(id, {
                  title: (title ?? bundle.title).trim(),
                  description: (description ?? bundle.description ?? '').trim() || null
                });
                setTitle(null);
                setDescription(null);
              }, 'Saved.')
            }
          >
            Save
          </Button>
        </div>
      </Panel>

      <Panel style={{ display: 'grid', gap: 10 }}>
        <PanelHead title="Courses" meta={bundle.ordered ? 'Learners unlock them in this order' : null} />

        {courses.map((entry, i) => (
          <div
            key={entry.course.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '10px 4px',
              borderTop: HAIR
            }}
          >
            <span style={{ ...monoPlain, ...figure, width: 22, textAlign: 'right' }}>{i + 1}</span>
            <CourseMark colour={entry.course.colour} size={30} />
            <Link to={`/course/${entry.course.id}`} style={{ flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-strong)' }}>
                {entry.course.title}
              </span>
            </Link>
            <IconButton
              icon="ArrowUp"
              label="Move up"
              size="sm"
              disabled={busy || i === 0}
              onClick={() => move(i, -1)}
            />
            <IconButton
              icon="ArrowDown"
              label="Move down"
              size="sm"
              disabled={busy || i === courses.length - 1}
              onClick={() => move(i, 1)}
            />
            <IconButton
              icon="X"
              label={`Remove ${entry.course.title}`}
              size="sm"
              disabled={busy}
              onClick={() =>
                setOrder(
                  order.filter((c) => c !== entry.course.id),
                  `${entry.course.title} is out of this bundle.`
                )
              }
            />
          </div>
        ))}

        {courses.length === 0 && <Footnote>Add the first course.</Footnote>}

        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', borderTop: HAIR, paddingTop: 14 }}>
          <Select
            label="Add a course"
            size="sm"
            value={adding}
            style={{ minWidth: 240 }}
            onChange={(e) => setAdding(e.target.value)}
            options={[
              { value: '', label: 'Choose a course' },
              ...notInBundle.map((row) => ({ value: row.card.id, label: row.card.title }))
            ]}
          />
          <Button
            variant="secondary"
            size="sm"
            disabled={busy || !adding}
            onClick={() => {
              const added = adding;
              setAdding('');
              setOrder([...order, added], 'Added to the bundle.');
            }}
          >
            Add
          </Button>
        </div>
      </Panel>

      {report.data && (
        <Panel style={{ display: 'grid', gap: 16 }}>
          <PanelHead title="Bundle report" />
          <StatRow>
            <Stat label="Learners" value={report.data.learners.toLocaleString()} />
            <Stat label="Completed the bundle" value={report.data.completed.toLocaleString()} />
          </StatRow>

          {report.data.courses.map((line) => (
            <div
              key={line.course.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 0',
                borderTop: HAIR
              }}
            >
              <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: 'var(--text-strong)' }}>
                {line.course.title}
              </span>
              <span style={{ ...monoPlain, ...figure }}>
                {meta(
                  `${line.learners} learners`,
                  `${line.completed} done`,
                  line.averageScore === null ? null : `score ${line.averageScore}%`
                )}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/teach/courses/${line.course.id}/report`)}
              >
                Report
              </Button>
            </div>
          ))}
        </Panel>
      )}

      {assigning && (
        <AssignDialog
          bundleId={bundle.id}
          subject={bundle.title}
          onClose={() => setAssigning(false)}
          onDone={(result) => {
            setAssigning(false);
            setOk(
              `Assigned to ${plural(result.assigned, 'person', 'people')}.${
                result.alreadyAssigned > 0 ? ` ${result.alreadyAssigned} already had it.` : ''
              }`
            );
            void reload();
          }}
        />
      )}
    </>
  );
}
